import config from '../config/database';
import tokenRefreshManager from '../utils/tokenRefreshManager';
import logger from '../utils/logger';

// API Service for database operations
class ApiService {
  constructor() {
    // Ensure config is loaded and has required properties
    if (!config) {
      logger.error('API Config is undefined! Check database.js config file.');
      throw new Error('API configuration is not available');
    }
    this.baseURL = config.baseURL || '/portal/api';
    this.baseTimeout = config.timeout || 10000; // Base timeout (10 seconds)
    this.timeout = this.baseTimeout; // Current timeout (adaptive)
    this.headers = config.headers || {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    this.lastUpdateCheck = 0;
    this.updateCheckInterval = 30000; // Check for updates every 30 seconds max
    
    // Token version tracking (to detect stale tokens)
    this.currentRequestTokenVersion = null;
    
    // CSRF token management
    this.csrfToken = null;
    this.csrfTokenExpiresAt = null;
    this.csrfTokenUserId = null; // Track user ID associated with CSRF token
    this.csrfTokenPromise = null; // Track in-flight CSRF token fetch
    
    // Adaptive timeout tracking for firewall detection
    this.requestLatencies = []; // Track last 10 request latencies
    this.maxLatencyHistory = 10;
    this.firewallDetected = false;
    this.slowNetworkDetected = false;
    
    // Initialize adaptive timeout based on network conditions
    this.updateAdaptiveTimeout();
  }
  
  /**
   * Detect slow network conditions and adjust timeout accordingly
   */
  updateAdaptiveTimeout() {
    // Check Network Information API if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      const effectiveType = connection.effectiveType;
      const downlink = connection.downlink;
      const rtt = connection.rtt;
      
      // Detect slow network conditions
      const isSlowNetwork = effectiveType === 'slow-2g' || 
                           effectiveType === '2g' ||
                           (effectiveType === '3g' && downlink < 1.5) ||
                           rtt > 1000; // RTT > 1 second
      
      this.slowNetworkDetected = isSlowNetwork;
      
      if (isSlowNetwork) {
        // Increase timeout for slow networks (30 seconds)
        this.timeout = 30000;
        logger.debug('Slow network detected, timeout increased to 30s');
      } else {
        // Use base timeout for normal networks
        this.timeout = this.baseTimeout;
      }
    }
    
      // Check if firewall might be present based on latency history
      if (this.requestLatencies.length >= 3) {
        const avgLatency = this.requestLatencies.reduce((a, b) => a + b, 0) / this.requestLatencies.length;
        const maxLatency = Math.max(...this.requestLatencies);
        
        // Firewall likely present if:
        // - Average latency > 2 seconds, OR
        // - Max latency > 5 seconds, OR
        // - High variance in latencies (suggests DPI)
        const variance = this.requestLatencies.reduce((acc, lat) => acc + Math.pow(lat - avgLatency, 2), 0) / this.requestLatencies.length;
        const isHighVariance = variance > 1000000; // Variance > 1 second squared
        
        if (avgLatency > 2000 || maxLatency > 5000 || isHighVariance) {
          if (!this.firewallDetected) {
            this.firewallDetected = true;
            logger.warn('Firewall detected based on latency patterns. Timeout increased.', { avgLatency, maxLatency, variance });
            // Dispatch custom event for UI to show warning
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('firewall-detected', { 
                detail: { avgLatency, maxLatency, variance } 
              }));
            }
          }
          // Increase timeout for firewall environments (25 seconds)
          this.timeout = Math.max(this.timeout, 25000);
        } else if (this.firewallDetected && avgLatency < 1000 && maxLatency < 2000) {
          // Reset firewall detection if latency improves
          this.firewallDetected = false;
          this.timeout = this.slowNetworkDetected ? 30000 : this.baseTimeout;
          logger.debug('Firewall detection reset - latency improved');
          // Dispatch event to clear warning
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('firewall-cleared'));
          }
        }
      }
    }
  
  /**
   * Record request latency for adaptive timeout
   */
  recordLatency(latency) {
    this.requestLatencies.push(latency);
    if (this.requestLatencies.length > this.maxLatencyHistory) {
      this.requestLatencies.shift(); // Keep only last N latencies
    }
    // Update adaptive timeout periodically
    if (this.requestLatencies.length % 3 === 0) {
      this.updateAdaptiveTimeout();
    }
  }

  /**
   * Check for app updates after API requests (debounced)
   */
  checkForUpdatesAfterRequest() {
    const now = Date.now();
    // Only check every 30 seconds to avoid excessive requests
    if (now - this.lastUpdateCheck < this.updateCheckInterval) {
      return;
    }
    
    this.lastUpdateCheck = now;
    
    // Dynamically import versionService to avoid circular dependencies
    import('./versionService').then(({ default: versionService }) => {
      if (versionService.shouldCheck()) {
        versionService.forceCheck();
      }
    }).catch(() => {
      // Silently fail if versionService is not available
    });
  }

  // Helper method to make public HTTP requests (no auth header)
  async publicRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: 'GET',
      headers: {
        ...this.headers,
        ...options.headers
      },
      ...options
    };

    // Create AbortController for timeout handling
    let controller = null;
    let timeoutId = null;
    
    // Only add timeout if not already provided in options
    if (!options.signal && !options.timeout) {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, this.timeout);
    }

    try {
      const fetchConfig = {
        ...config,
        ...(controller && { signal: controller.signal })
      };
      
      const response = await fetch(url, fetchConfig);
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            logger.error('API Error Details', { errorData, status: response.status, url });
          } else {
            const textResponse = await response.text();
            // Try to parse as JSON in case content-type header is wrong
            if (textResponse && textResponse.trim().startsWith('{')) {
              try {
                const parsedJson = JSON.parse(textResponse);
                errorMessage = parsedJson.message || parsedJson.error || textResponse;
                errorData = parsedJson;
              } catch (jsonParseError) {
                // Not valid JSON, use as-is
                errorMessage = textResponse || errorMessage;
              }
            } else {
              errorMessage = textResponse || errorMessage;
            }
            logger.error('Non-JSON error response', { textResponse, status: response.status, url });
          }
        } catch (parseError) {
          logger.error('Could not parse error response', { parseError, status: response.status, url });
          if (response.status === 429) {
            errorMessage = 'Too many requests. Please wait a moment and try again.';
          }
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        if (errorData) {
          error.errorData = errorData;
        }
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Handle timeout errors
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        const timeoutError = new Error('Request timed out. Please check your internet connection and try again.');
        timeoutError.status = 408;
        timeoutError.isTimeout = true;
        logger.error('API request timed out', { timeout: this.timeout, url });
        throw timeoutError;
      }
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        const networkError = new Error('Network error. Please check your internet connection.');
        networkError.status = 0;
        networkError.isNetworkError = true;
        logger.error('API network error', { error, url });
        throw networkError;
      }

      logger.error('API request failed', { error, url });
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Get user ID from JWT token
   * @returns {number|null} User ID or null if token invalid
   */
  getUserIdFromToken(token) {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Proper base64url decoding (JWT uses URL-safe base64)
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      
      // Backend uses 'userId' field (see server/routes/auth.js:84)
      return payload.userId || null;
    } catch (error) {
      logger.warn('Error decoding token to get user ID', { error });
      return null;
    }
  }

  /**
   * Fetch CSRF token from server
   * @param {number} retryCount - Internal retry counter to prevent infinite loops
   * @returns {Promise<string|null>} CSRF token or null if not authenticated
   */
  async fetchCSRFToken(retryCount = 0) {
    // Prevent infinite recursion (max 2 retries)
    if (retryCount > 2) {
      logger.error('CSRF token fetch exceeded retry limit', { retryCount });
      return null;
    }

    // If already fetching, wait for that promise
    if (this.csrfTokenPromise) {
      return this.csrfTokenPromise;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('faithinsight_token');
    if (!token) {
      return null;
    }

    // Get current user ID from token
    const currentUserId = this.getUserIdFromToken(token);
    
    // Handle invalid token
    if (currentUserId === null) {
      logger.warn('Invalid token, clearing CSRF token');
      this.csrfToken = null;
      this.csrfTokenExpiresAt = null;
      this.csrfTokenUserId = null;
      return null;
    }
    
    // If cached token exists, check if user ID matches
    if (this.csrfToken && this.csrfTokenExpiresAt && Date.now() < this.csrfTokenExpiresAt) {
      // If user ID changed, clear old CSRF token
      if (this.csrfTokenUserId !== null && this.csrfTokenUserId !== currentUserId) {
        logger.debug('User ID changed, clearing old CSRF token', { oldUserId: this.csrfTokenUserId, newUserId: currentUserId });
        this.csrfToken = null;
        this.csrfTokenExpiresAt = null;
        this.csrfTokenUserId = null;
      } else if (this.csrfTokenUserId === currentUserId) {
        // User ID matches, return cached token
        return this.csrfToken;
      }
    }

    // Fetch new CSRF token
    this.csrfTokenPromise = (async () => {
      try {
        // Use adaptive timeout for CSRF token fetch (same as API requests)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, this.timeout);
        
        const response = await fetch(`${this.baseURL}/csrf-token`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          // If 401, check if it's a token expiration issue
          if (response.status === 401) {
            // Try to parse error to see if it's token expiration
            let errorData = null;
            try {
              errorData = await response.json();
            } catch (e) {
              // Not JSON, treat as generic 401
            }
            
            // Check if this is a token expiration (not just missing auth)
            const isTokenExpired = errorData && (
              errorData.error === 'Token expired' || 
              errorData.error === 'Invalid token' ||
              errorData.message?.includes('expired') ||
              errorData.message?.includes('Token expired') ||
              errorData.message?.includes('Invalid token')
            );
            
            // If token is expired, wait for refresh and retry
            if (isTokenExpired) {
              try {
                // Check if refresh is already in progress
                if (tokenRefreshManager.isRefreshInProgress()) {
                  logger.debug('[CSRF Token] Token refresh in progress, waiting...');
                  await tokenRefreshManager.waitForRefresh();
                  // Retry CSRF token fetch with new token
                  this.csrfTokenPromise = null;
                  return this.fetchCSRFToken(retryCount + 1);
                } else {
                  // Perform refresh with locking
                  logger.debug('[CSRF Token] Token expired, refreshing...');
                  await tokenRefreshManager.refreshTokenWithLock(this.baseURL);
                  // Retry CSRF token fetch with new token
                  this.csrfTokenPromise = null;
                  return this.fetchCSRFToken(retryCount + 1);
                }
              } catch (refreshError) {
                // Refresh failed, clear tokens and return null
                logger.error('[CSRF Token] Token refresh failed', { refreshError });
                this.csrfToken = null;
                this.csrfTokenExpiresAt = null;
                this.csrfTokenUserId = null;
                return null;
              }
            } else {
              // Not a token expiration, just clear and return null
              this.csrfToken = null;
              this.csrfTokenExpiresAt = null;
              this.csrfTokenUserId = null;
              return null;
            }
          }
          
          // Retry on 500/503 errors (server errors)
          if ((response.status === 500 || response.status === 503) && retryCount < 2) {
            logger.debug(`CSRF Token server error ${response.status}, retrying`, { attempt: retryCount + 1, maxAttempts: 2 });
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            this.csrfTokenPromise = null;
            return this.fetchCSRFToken(retryCount + 1);
          }
          
          throw new Error(`Failed to fetch CSRF token: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.csrfToken) {
          // Verify user ID still matches (token might have refreshed during fetch)
          const verifyUserId = this.getUserIdFromToken(localStorage.getItem('faithinsight_token'));
          if (verifyUserId !== null && verifyUserId !== currentUserId) {
            logger.debug('User ID changed during CSRF fetch, clearing token and retrying', { oldUserId: currentUserId, newUserId: verifyUserId });
            this.csrfToken = null;
            this.csrfTokenExpiresAt = null;
            this.csrfTokenUserId = null;
            // Retry fetch with new user ID (with retry counter to prevent infinite loops)
            this.csrfTokenPromise = null;
            return this.fetchCSRFToken(retryCount + 1);
          }
          
          this.csrfToken = data.csrfToken;
          // Set expiry to 23 hours (slightly less than server's 24 hours)
          this.csrfTokenExpiresAt = Date.now() + (23 * 60 * 60 * 1000);
          // Store user ID associated with this CSRF token
          this.csrfTokenUserId = currentUserId;
          return this.csrfToken;
        }

        return null;
      } catch (error) {
        // Handle timeout and network errors with retry
        const isNetworkError = error.name === 'AbortError' || 
                              error.name === 'TypeError' || 
                              error.message?.includes('fetch') ||
                              error.message?.includes('timeout');
        
        if (isNetworkError && retryCount < 2) {
          logger.debug(`CSRF Token network error, retrying`, { attempt: retryCount + 1, maxAttempts: 2, error: error.message });
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          this.csrfTokenPromise = null;
          return this.fetchCSRFToken(retryCount + 1);
        }

        logger.error('Error fetching CSRF token', { error, retryCount });
        return null;
      } finally {
        this.csrfTokenPromise = null;
      }
    })();

    return this.csrfTokenPromise;
  }

  /**
   * Clear CSRF token (call on logout)
   */
  clearCSRFToken() {
    this.csrfToken = null;
    this.csrfTokenExpiresAt = null;
    this.csrfTokenUserId = null;
    this.csrfTokenPromise = null;
  }

  // Helper method to make HTTP requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: 'GET',
      headers: {
        ...this.headers,
        ...options.headers
      },
      timeout: this.timeout,
      ...options
    };

    // Log request initiation for Apple Pay debugging
    if (endpoint.includes('/confirm-payment')) {
      console.log('🍎 [APPLE PAY] API request initiated', {
        endpoint,
        url,
        method: config.method,
        hasBody: !!options.body,
        timestamp: new Date().toISOString()
      });
    }

    // Add authorization header if token exists
    // Track token version to detect stale tokens
    const token = localStorage.getItem('faithinsight_token');
    const tokenVersion = tokenRefreshManager.getTokenVersion();
    this.currentRequestTokenVersion = tokenVersion;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Token-Version'] = tokenVersion.toString();
      
      // Add CSRF token for state-changing requests
      const isStateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes((options.method || 'GET').toUpperCase());
      if (isStateChanging) {
          const csrfToken = await this.fetchCSRFToken();
          if (!csrfToken) {
            // Enhanced error message for CSRF token fetch failure
            const error = new Error('Failed to obtain CSRF token. This may be due to a network issue. Please check your connection and try again, or refresh the page.');
            error.csrfTokenMissing = true;
            error.retryable = true; // Mark as retryable
            error.isNetworkError = true; // Indicate it might be a network issue
            // Only log as error if token refresh is not in progress (expected scenario)
            if (!tokenRefreshManager.isRefreshInProgress()) {
              logger.error('CSRF token fetch failed - request cannot proceed without CSRF token', { endpoint, url });
            } else {
              logger.debug('CSRF token fetch failed but token refresh in progress (expected)', { endpoint, url });
            }
            throw error;
          }
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    } else {
    }

    // Create AbortController for timeout handling
    let controller = null;
    let timeoutId = null;
    
    // Only add timeout if not already provided in options
    if (!options.signal && !options.timeout) {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, this.timeout);
    }

    try {
      const fetchConfig = {
        ...config,
        ...(controller && { signal: controller.signal })
      };
      
      const response = await fetch(url, fetchConfig);
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData = null;
        
        // Clone the response so we can read it multiple times if needed
        const responseClone = response.clone();
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            
            // Handle email change requiring re-authentication
            if (errorData.requiresReauth) {
              // Check if user is on a quiz page - don't logout during active quiz attempts
              const isOnQuizPage = window.location.pathname.includes('/student/attempt/');
              
              if (isOnQuizPage) {
                // During quiz, don't logout - let the quiz component handle the error
                logger.debug('Email change detected during quiz attempt - not logging out to prevent data loss');
                throw new Error('Email change detected. Please save your answers and refresh the page.');
              }

              logger.info('Email changed, forcing logout and redirect to login');
              // Clear local storage
              localStorage.removeItem('faithinsight_user');
              localStorage.removeItem('faithinsight_token');
              
              // Clear CSRF token
              this.clearCSRFToken();
              
              // Redirect to login page
              window.location.href = '/portal/login';
              return;
            }
            
            // Handle invalid/expired authentication tokens
            if (response.status === 401) {
              // Check if this is a login request - don't treat as token expiration
              // Login 401 errors are just wrong credentials, not expired tokens
              const isLoginRequest = endpoint.includes('/auth/login') || url.includes('/auth/login');
              
              // Check if this is a refresh token request - don't treat as session expiration
              // Refresh token failures are handled separately
              const isRefreshRequest = endpoint.includes('/auth/refresh') || url.includes('/auth/refresh');
              
              // Check if this is a signup request - don't treat as token expiration
              // Signup 401 errors are validation errors, not expired tokens
              const isSignupRequest = endpoint.includes('/auth/signup') || url.includes('/auth/signup');
              
              // Check if this is a password reset/forgot password request - don't treat as token expiration
              const isPasswordResetRequest = endpoint.includes('/auth/reset-password') || 
                                            endpoint.includes('/auth/forgot-password') ||
                                            url.includes('/auth/reset-password') ||
                                            url.includes('/auth/forgot-password');
              
              // Check if this is a CSRF token request - don't treat as session expiration
              // CSRF token 401 errors are just missing auth, not expired tokens
              const isCSRFRequest = endpoint.includes('/csrf-token') || url.includes('/csrf-token');
              
              if (isLoginRequest) {
                // For login requests, don't clear tokens or set session expired flag
                // Just throw the error so the login component can handle it (wrong credentials)
                // Prioritize message over error for more user-friendly error display
                throw new Error(errorData.message || errorData.error || 'Invalid credentials');
              }
              
              if (isRefreshRequest || isSignupRequest || isPasswordResetRequest || isCSRFRequest) {
                // For these requests, don't clear tokens or set session expired flag
                // Just throw the error so the calling component can handle it appropriately
                // Prioritize message over error for more user-friendly error display
                throw new Error(errorData.message || errorData.error || 'Authentication failed');
              }
              
              // Check if this is a token expiration (not other auth errors)
              // Improved detection: check status code first, then error message
              const isTokenExpired = response.status === 401 && (
                errorData.error === 'Token expired' || 
                errorData.error === 'Invalid token' ||
                errorData.message?.includes('expired') ||
                errorData.message?.includes('session has expired') ||
                errorData.message?.includes('Token expired') ||
                errorData.message?.includes('Invalid token')
              );
              
              // Try to refresh token if it's expired
              if (isTokenExpired) {
                try {
                  // Check if refresh is already in progress
                  if (tokenRefreshManager.isRefreshInProgress()) {
                    logger.debug('Token refresh already in progress, waiting...');
                    await tokenRefreshManager.waitForRefresh();
                    // Clear CSRF token after token refresh to prevent user mismatch
                    this.clearCSRFToken();
                    logger.debug('Cleared CSRF token after token refresh (wait path)');
                    // PRIORITY 6: Retry with new token (no timeout for retries)
                    const newToken = localStorage.getItem('faithinsight_token');
                    if (newToken) {
                      const newOptions = {
                        ...options,
                        timeout: null, // No timeout for retry requests (they might take longer)
                        headers: {
                          ...options.headers,
                          'Authorization': `Bearer ${newToken}`,
                          'X-Token-Version': tokenRefreshManager.getTokenVersion().toString()
                        }
                      };
                      return this.request(endpoint, newOptions);
                    }
                  } else {
                    // Perform refresh with locking
                    logger.debug('Attempting to refresh expired access token...');
                    const refreshResult = await tokenRefreshManager.refreshTokenWithLock(this.baseURL);

                    if (refreshResult && refreshResult.token) {
                      logger.info('Token refreshed successfully, retrying original request');

                      // Clear CSRF token after token refresh to prevent user mismatch
                      // CSRF token is tied to user ID, so we need a fresh token after auth token refresh
                      this.clearCSRFToken();
                      logger.debug('Cleared CSRF token after token refresh');

                      // CRITICAL FIX: Fetch new CSRF token before retrying request
                      // This ensures the retry request has a valid CSRF token
                      try {
                        await this.fetchCSRFToken();
                        logger.debug('Fetched new CSRF token after token refresh');
                      } catch (csrfError) {
                        logger.error('Failed to fetch CSRF token after token refresh', { csrfError });
                        // Continue anyway - fetchCSRFToken will be called again in request() method
                        // But it's better to have it ready now
                      }
                      
                      // Check if token version changed during request (stale token)
                      const newTokenVersion = tokenRefreshManager.getTokenVersion();
                      if (this.currentRequestTokenVersion !== null &&
                          this.currentRequestTokenVersion !== newTokenVersion) {
                        logger.debug('Token was refreshed during request, retrying with new token', { oldVersion: this.currentRequestTokenVersion, newVersion: newTokenVersion });
                      }
                      
                      // PRIORITY 6: Retry original request with new token (no timeout for retries)
                      const newOptions = {
                        ...options,
                        timeout: null, // No timeout for retry requests (they might take longer)
                        headers: {
                          ...options.headers,
                          'Authorization': `Bearer ${refreshResult.token}`,
                          'X-Token-Version': newTokenVersion.toString()
                        }
                      };
                      return this.request(endpoint, newOptions);
                    }
                  }
                } catch (refreshError) {
                  logger.error('Token refresh failed', { refreshError, endpoint, url });
                  
                  // Handle specific refresh errors
                  if (refreshError.refreshTokenExpired || refreshError.noRefreshToken) {
                    // Refresh token expired - cannot recover
                    const error = new Error('Your session has expired. Please log in again to continue.');
                    error.refreshTokenExpired = true;
                    throw error;
                  }
                  
                  if (refreshError.rateLimited) {
                    // Rate limited - wait and retry
                    const error = new Error('Too many refresh attempts. Please wait a moment and try again.');
                    error.rateLimited = true;
                    throw error;
                  }
                  
                  // Other refresh errors - throw original error
                  throw refreshError;
                }
              }
              
              // Check if user is on a quiz page - don't logout during active quiz attempts
              // This prevents data loss of unsaved quiz answers
              const isOnQuizPage = window.location.pathname.includes('/student/attempt/');
              
              if (isOnQuizPage) {
                // During quiz, don't logout - let the quiz component handle the error
                // This allows answers to be saved before session expires
                logger.debug('Token expired during quiz attempt - not logging out to prevent data loss');
                // Throw error so quiz component can handle it gracefully
                throw new Error('Session expired. Please save your answers and refresh the page.');
              }

              // Check if this is a logout request - don't set session expired flag for deliberate logout
              const isLogoutRequest = endpoint.includes('/auth/logout') || url.includes('/auth/logout');
              
              logger.warn('Invalid or expired authentication token, forcing logout', { status: response.status, errorData, url, isLogoutRequest });
              
              // Clear tokens and force logout for 401 (authentication failure)
              localStorage.removeItem('faithinsight_user');
              localStorage.removeItem('faithinsight_token');
              localStorage.removeItem('faithinsight_refresh_token');
              
              // Clear CSRF token
              this.clearCSRFToken();
              
              if (isLogoutRequest) {
                // For logout requests, set logged out flag (not session expired)
                // This shows "You have been logged out" message instead of "session expired"
                localStorage.setItem('faithinsight_logged_out', 'true');
                // Throw error - let the logout handler in AuthContext handle navigation
                throw new Error('Logout failed: Token expired');
              } else {
                // For non-logout requests, set session expired flag
                localStorage.setItem('faithinsight_session_expired', 'true');
                // Force redirect to login page
                window.location.href = '/portal/login';
              }
              return;
            }
            
            // Handle insufficient permissions (403) - don't logout, just throw error
            if (response.status === 403) {
              logger.warn('Insufficient permissions for this resource', { status: response.status, errorData, url });

              // Check if it's a CSRF-related error
              if (errorData.error === 'CSRF token mismatch' ||
                  errorData.error === 'Invalid CSRF token' ||
                  errorData.error === 'CSRF token expired' ||
                  errorData.error === 'CSRF token missing' ||
                  errorData.message?.includes('CSRF')) {
                logger.debug('CSRF error detected, clearing token and retrying once');
                this.clearCSRFToken();
                
                // Retry the request once with a fresh CSRF token
                const retryCount = options.csrfRetryCount || 0;
                if (retryCount < 1) {
                  try {
                    // Fetch a new CSRF token
                    await this.fetchCSRFToken();
                    // Retry the request with the new token
                    return this.request(endpoint, {
                      ...options,
                      csrfRetryCount: retryCount + 1
                    });
                  } catch (csrfError) {
                    logger.error('Failed to fetch CSRF token for retry', { csrfError });
                    // If CSRF token fetch fails, throw the original error
                  }
                }
              }
              
              // Don't logout for 403 - this is a permission issue, not auth issue
              // Just throw the error to be handled by the calling component
              const error = new Error(errorMessage);
              error.status = response.status;
              error.response = errorData;
              throw error;
            }
          } else {
            // Handle non-JSON responses (like rate limiting or CSRF errors)
            try {
              const textResponse = await responseClone.text();
              // Try to parse as JSON in case content-type header is wrong
              if (textResponse && textResponse.trim().startsWith('{')) {
                try {
                  const parsedJson = JSON.parse(textResponse);
                  errorMessage = parsedJson.message || parsedJson.error || textResponse;
                  errorData = parsedJson;
                } catch (jsonParseError) {
                  // Not valid JSON, use as-is
                  errorMessage = textResponse || errorMessage;
                }
              } else {
                errorMessage = textResponse || errorMessage;
              }
              logger.error('Non-JSON error response', { textResponse, status: response.status, url });
            } catch (textError) {
              // If we can't read the text either, use a default message based on status
              if (response.status === 403) {
                errorMessage = 'Access denied. You may not have permission to perform this action.';
              } else if (response.status === 429) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
              } else {
                errorMessage = `HTTP error ${response.status}: ${response.statusText || 'Unknown error'}`;
              }
              logger.error('Could not read error response body', { textError, status: response.status, url });
            }
          }
        } catch (parseError) {
          logger.error('Could not parse error response', { parseError, status: response.status, url });
          // Try to get text response as fallback
          try {
            const textResponse = await responseClone.text();
            if (textResponse) {
              // Try to parse as JSON in case content-type header is wrong
              if (textResponse.trim().startsWith('{')) {
                try {
                  const parsedJson = JSON.parse(textResponse);
                  errorMessage = parsedJson.message || parsedJson.error || textResponse;
                  errorData = parsedJson;
                } catch (jsonParseError) {
                  // Not valid JSON, use as-is
                  errorMessage = textResponse;
                }
              } else {
                errorMessage = textResponse;
              }
            }
          } catch (textError) {
            // If we can't read the text either, use a default message based on status
            if (response.status === 403) {
              errorMessage = 'Access denied. You may not have permission to perform this action.';
            } else if (response.status === 429) {
              errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else {
              errorMessage = `HTTP error ${response.status}: ${response.statusText || 'Unknown error'}`;
            }
          }
        }
        
        // Log error details for Apple Pay debugging
        if (endpoint.includes('/confirm-payment')) {
          console.error('🍎 [APPLE PAY] API request failed', {
            url,
            status: response.status,
            statusText: response.statusText,
            errorMessage,
            errorData,
            timestamp: new Date().toISOString()
          });
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      
      // Only try to parse JSON if response has content
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        // Still check for updates even on 204 responses
        this.checkForUpdatesAfterRequest();
        return null; // No content response
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        
        // Log successful response for Apple Pay debugging
        if (endpoint.includes('/confirm-payment')) {
          console.log('🍎 [APPLE PAY] API response successful', {
            url,
            status: response.status,
            hasData: !!data,
            dataKeys: data ? Object.keys(data) : [],
            timestamp: new Date().toISOString()
          });
        }
        
        // Check for updates after successful API call
        this.checkForUpdatesAfterRequest();
        return data;
      } else {
        // Check for updates even on non-JSON responses
        this.checkForUpdatesAfterRequest();
        // Non-JSON response, return text or null
        const text = await response.text();
        this.checkForUpdatesAfterRequest();
        return text || null;
      }
    } catch (error) {
      // Handle timeout errors
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        const timeoutError = new Error('Request timed out. Please check your internet connection and try again.');
        timeoutError.status = 408;
        timeoutError.isTimeout = true;
        timeoutError.retryable = true; // Mark as retryable
        logger.error('API request timed out', { timeout: this.timeout, url, endpoint });
        
        // MEDIUM ISSUE 16 FIX: Retry logic for critical operations
        // For critical operations (quiz saves, submissions), allow retry
        const isCriticalOperation = url.includes('/quizzes/student/attempts/') && 
          (url.includes('/response') || url.includes('/submit'));
        
        if (isCriticalOperation && options.retryOnTimeout !== false) {
          // Retry once for critical operations
          const retryCount = options.retryCount || 0;
          if (retryCount < 1) {
            logger.debug('Retrying critical operation after timeout', { attempt: retryCount + 1, endpoint, url });
            // Wait 1 second before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
            return this.request(endpoint, {
              ...options,
              retryCount: retryCount + 1,
              timeout: this.timeout * 1.5 // Increase timeout for retry
            });
          }
        }
        
        throw timeoutError;
      }
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        const networkError = new Error('Network error. Please check your internet connection.');
        networkError.status = 0;
        networkError.isNetworkError = true;
        logger.error('API network error', { error, url });
        throw networkError;
      }

      logger.error('API request failed', { error, url });
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  // Authentication endpoints
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  async validateToken() {
    return this.request('/auth/validate-token', {
      method: 'GET'
    });
  }

  async signup(userData) {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async logout() {
    // Clear CSRF token on logout
    this.clearCSRFToken();
    
    return this.request('/auth/logout', {
      method: 'POST'
    });
  }

  async changePassword(passwordData) {
    return this.request('/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData)
    });
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem('faithinsight_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken })
    });
  }

  async forgotUsername(firstName, lastName) {
    return this.request('/auth/forgot-username', {
      method: 'POST',
      body: JSON.stringify({ firstName, lastName })
    });
  }

  async forgotPassword(email) {
    
    const result = await this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    
    return result;
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  }


  async updateUserProfile(userData) {
    return this.request('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }


  // Course endpoints
  async getCourses() {
    return this.request('/courses');
  }

  async getStudents() {
    return this.request('/users/students');
  }

  async getStudentsByCourse(courseId) {
    return this.request(`/users/students/course/${courseId}`);
  }

  async getClassmatesByCourse(courseId) {
    return this.request(`/users/students/course/${courseId}/classmates`);
  }

  async getCoursesForStudents() {
    return this.request('/users/courses-for-students');
  }

  async getPublicCourses() {
    return this.request('/courses/catalog');
  }

  async getCoursePricing(courseId) {
    return this.publicRequest(`/payments/course/${courseId}/pricing`);
  }

  async updateCoursePricing(courseId, pricingData) {
    return this.request(`/payments/course/${courseId}/pricing`, {
      method: 'PUT',
      body: JSON.stringify(pricingData)
    });
  }

  // Payment methods
  async createPaymentIntent(paymentData) {
    return this.request('/payments/create-payment-intent-authenticated', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }


  async checkEnrollment(courseIds, email) {
    return this.publicRequest('/payments/check-enrollment', {
      method: 'POST',
      body: JSON.stringify({ courseIds, email })
    });
  }

  async confirmPayment(paymentData) {
    return this.request('/payments/confirm-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Promo codes API methods
  async getPromoCodes() {
    return this.request('/promo-codes');
  }

  async getActivePromoCodes() {
    return this.publicRequest('/promo-codes/active');
  }

  async validatePromoCode(code, courseIds = [], productIds = []) {
    // Use authenticated request since promo-codes routes require authentication
    // Users should be authenticated when on the payment form
    return this.request('/promo-codes/validate', {
      method: 'POST',
      body: JSON.stringify({ code, courseIds, productIds })
    });
  }

  async createPromoCode(promoData) {
    return this.request('/promo-codes', {
      method: 'POST',
      body: JSON.stringify(promoData)
    });
  }

  async updatePromoCode(id, promoData) {
    return this.request(`/promo-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(promoData)
    });
  }

  async deletePromoCode(id) {
    return this.request(`/promo-codes/${id}`, {
      method: 'DELETE'
    });
  }

  // Bulk pricing API methods
  async getBulkPricingRules() {
    return this.request('/bulk-pricing');
  }

  async getActiveBulkPricingRules() {
    return this.publicRequest('/bulk-pricing/active');
  }

  async calculateBulkPricing(courseIds) {
    // Use authenticated request since bulk-pricing routes require authentication
    // Users should be authenticated when on the payment form
    return this.request('/bulk-pricing/calculate', {
      method: 'POST',
      body: JSON.stringify({ courseIds })
    });
  }

  async createBulkPricingRule(ruleData) {
    return this.request('/bulk-pricing', {
      method: 'POST',
      body: JSON.stringify(ruleData)
    });
  }

  async updateBulkPricingRule(id, ruleData) {
    return this.request(`/bulk-pricing/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData)
    });
  }

  async deleteBulkPricingRule(id) {
    return this.request(`/bulk-pricing/${id}`, {
      method: 'DELETE'
    });
  }

  // Additional products API methods
  async getAdditionalProducts() {
    return this.request('/additional-products');
  }

  async getActiveAdditionalProducts() {
    return this.request('/additional-products/active');
  }

  async getAdditionalProduct(id) {
    // FIX: Use authenticated request since the route requires authentication (global middleware)
    return this.request(`/additional-products/${id}`);
  }

  async createAdditionalProduct(productData) {
    return this.request('/additional-products', {
      method: 'POST',
      body: JSON.stringify(productData)
    });
  }

  async updateAdditionalProduct(id, productData) {
    return this.request(`/additional-products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
  }

  async deleteAdditionalProduct(id) {
    return this.request(`/additional-products/${id}`, {
      method: 'DELETE'
    });
  }

  async getProductCategories() {
    // FIX: Use authenticated request since the route requires authentication (global middleware)
    return this.request('/additional-products/categories/all');
  }

  async createProductCategory(categoryData) {
    return this.request('/additional-products/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async getStudentSponsorships() {
    return this.request('/additional-products/sponsorships/all');
  }

  async getCourse(courseId) {
    return this.request(`/courses/${courseId}`);
  }

  async enrollInCourse(courseId) {
    return this.request(`/courses/${courseId}/enroll`, {
      method: 'POST'
    });
  }

  async assignStudentToCourse(courseId, studentId, status = 'active') {
    return this.request(`/courses/${courseId}/assign-student`, {
      method: 'POST',
      body: JSON.stringify({ studentId, status })
    });
  }

  async removeStudentFromCourse(courseId, studentId) {
    return this.request(`/courses/${courseId}/remove-student/${studentId}`, {
      method: 'DELETE'
    });
  }

  async getCourseProgress(courseId) {
    return this.request(`/courses/${courseId}/progress`);
  }

  // Elective endpoints
  async getElectives() {
    return this.request('/electives');
  }

  async getElective(electiveId) {
    return this.request(`/electives/${electiveId}`);
  }

  async enrollInElective(electiveId) {
    return this.request(`/electives/${electiveId}/enroll`, {
      method: 'POST'
    });
  }

  async assignStudentToElective(electiveId, studentId, status = 'active') {
    return this.request(`/electives/${electiveId}/assign-student`, {
      method: 'POST',
      body: JSON.stringify({ studentId, status })
    });
  }

  async removeStudentFromElective(electiveId, studentId) {
    return this.request(`/electives/${electiveId}/remove-student/${studentId}`, {
      method: 'DELETE'
    });
  }

  async createElective(electiveData) {
    return this.request('/electives', {
      method: 'POST',
      body: JSON.stringify(electiveData)
    });
  }

  async updateElective(electiveId, electiveData) {
    return this.request(`/electives/${electiveId}`, {
      method: 'PUT',
      body: JSON.stringify(electiveData)
    });
  }

  async deleteElective(electiveId) {
    return this.request(`/electives/${electiveId}`, {
      method: 'DELETE'
    });
  }

  // Assignment endpoints
  async getAssignments(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/assignments?${queryParams}`);
  }

  async getAssignment(assignmentId) {
    return this.request(`/assignments/${assignmentId}`);
  }

  async submitAssignment(assignmentId, submissionData) {
    return this.request(`/assignments/${assignmentId}/submit`, {
      method: 'POST',
      body: JSON.stringify({
        id: assignmentId,
        ...submissionData
      })
    });
  }

  async saveAssignmentDraft(assignmentId, content, pdfAttachment = null, weekNumber = null) {
    return this.request(`/assignments/${assignmentId}/draft`, {
      method: 'POST',
      body: JSON.stringify({ content, pdf_attachment: pdfAttachment, weekNumber })
    });
  }

  async getAssignmentSubmission(assignmentId) {
    return this.request(`/assignments/${assignmentId}/submission`);
  }

  async getMyAssignmentSubmissions(assignmentId) {
    return this.request(`/assignments/${assignmentId}/my-submissions`);
  }

  async allowStudentResubmission(assignmentId, userId, weekNumber = null) {
    const url = `/assignments/${assignmentId}/allow-resubmission/${userId}`;
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify({ weekNumber })
    });
  }

  async createAssignment(assignmentData) {
    return this.request('/assignments', {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    });
  }

  async deleteAssignment(assignmentId) {
    return this.request(`/assignments/${assignmentId}`, {
      method: 'DELETE'
    });
  }

  async updateAssignment(assignmentId, assignmentData) {
    return this.request(`/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(assignmentData)
    });
  }

  // Instructor assignment methods
  async getAssignmentSubmissions(assignmentId) {
    return this.request(`/assignments/${assignmentId}/submissions`);
  }

  async gradeAssignment(assignmentId, submissionId, gradeData) {
    return this.request(`/assignments/${assignmentId}/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify(gradeData)
    });
  }

  // Weekly instructions methods
  async getWeeklyInstructions(assignmentId) {
    return this.request(`/assignments/${assignmentId}/weekly-instructions`);
  }

  async getWeeklyInstruction(assignmentId, weekNumber) {
    return this.request(`/assignments/${assignmentId}/weekly-instructions/${weekNumber}`);
  }

  async saveWeeklyInstruction(assignmentId, weekNumber, instructions) {
    return this.request(`/assignments/${assignmentId}/weekly-instructions`, {
      method: 'PUT',
      body: JSON.stringify({ weekNumber, instructions })
    });
  }

  async deleteWeeklyInstruction(assignmentId, weekNumber) {
    return this.request(`/assignments/${assignmentId}/weekly-instructions/${weekNumber}`, {
      method: 'DELETE'
    });
  }

  // Break periods endpoints
  async getBreakPeriods(assignmentId) {
    return this.request(`/assignments/${assignmentId}/break-periods`);
  }

  async createBreakPeriod(assignmentId, breakData) {
    return this.request(`/assignments/${assignmentId}/break-periods`, {
      method: 'POST',
      body: JSON.stringify(breakData)
    });
  }

  async updateBreakPeriod(assignmentId, breakId, breakData) {
    return this.request(`/assignments/${assignmentId}/break-periods/${breakId}`, {
      method: 'PUT',
      body: JSON.stringify(breakData)
    });
  }

  async deleteBreakPeriod(assignmentId, breakId) {
    return this.request(`/assignments/${assignmentId}/break-periods/${breakId}`, {
      method: 'DELETE'
    });
  }

  // Duplicate createAssignment method removed

  // Grade endpoints
  async getGrades() {
    return this.request('/grades');
  }

  async getCourseGrades(courseId) {
    return this.request(`/grades/course/${courseId}`);
  }

  async getAssignmentGrade(assignmentId) {
    return this.request(`/grades/assignment/${assignmentId}`);
  }

  async getCourseFinalGrades() {
    return this.request('/grades/course-final-grades');
  }

  // Calendar endpoints
  async getEvents(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/events?${queryParams}`);
  }

  async createEvent(eventData) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  }

  async updateEvent(eventId, eventData) {
    return this.request(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData)
    });
  }

  async deleteEvent(eventId) {
    return this.request(`/events/${eventId}`, {
      method: 'DELETE'
    });
  }

  // File upload endpoints
  async uploadFile(file, type = 'assignment') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return this.request('/upload', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it with boundary
        'Content-Type': undefined
      },
      body: formData
    });
  }

  async downloadFile(fileId) {
    return this.request(`/files/${fileId}/download`, {
      method: 'GET'
    });
  }

  // Notification endpoints
  async getNotifications() {
    return this.request('/notifications');
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT'
    });
  }

  // Search endpoints
  async search(query, type = 'all') {
    return this.request(`/search?q=${encodeURIComponent(query)}&type=${type}`);
  }

  // Dashboard endpoints
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  async getInstructorStats() {
    return this.request('/dashboard/instructor-stats');
  }

  async getRecentActivity() {
    return this.request('/dashboard/activity');
  }

  async getUpcomingDeadlines() {
    return this.request('/dashboard/deadlines');
  }

  // Students endpoints (for instructors/admins)

  async getStudentProfile(studentId) {
    return this.request(`/students/${studentId}`);
  }

  async getGradebook(courseId = null) {
    const query = courseId ? `?courseId=${courseId}` : '';
    return this.request(`/students/gradebook/all${query}`);
  }

  async overrideAssignmentGrade(assignmentId, submissionId, grade, feedback, override, overrideNotes) {
    return this.request(`/assignments/${assignmentId}/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ grade, feedback, override, overrideNotes })
    });
  }

  async gradeUnsubmittedAssignment(assignmentId, studentId, grade, feedback, override, overrideNotes) {
    return this.request(`/assignments/${assignmentId}/students/${studentId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ grade, feedback, override, overrideNotes })
    });
  }

  async assignCourseGrade(courseId, studentId, grade, notes, numericGrade = null) {
    return this.request(`/courses/${courseId}/students/${studentId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ grade, notes, numericGrade })
    });
  }

  async overrideQuizGrade(assignmentId, attemptId, score, notes) {
    // Only allow overriding grades for submitted quizzes (must have attemptId)
    if (!attemptId) {
      throw new Error('Cannot grade quiz: Quiz must be submitted before it can be graded');
    }
    
    // Override existing attempt score
    return this.request(`/quizzes/attempts/${attemptId}/score/override`, {
      method: 'PUT',
      body: JSON.stringify({ score, notes })
    });
  }

  async gradeUnsubmittedQuiz(assignmentId, studentId, score, notes) {
    return this.request(`/quizzes/assignments/${assignmentId}/students/${studentId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ score, notes })
    });
  }

  // Additional utility methods
  async getCurrentUserProfile() {
    return this.request('/users/profile');
  }

  getCurrentUser() {
    // Get current user from localStorage (for client-side use)
    const userStr = localStorage.getItem('faithinsight_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  async updateProfile(profileData) {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  // User management endpoints (for admins)
  async getUsers() {
    return this.request('/users');
  }

  async getUsersWithDefaultPasswords() {
    return this.request('/users/default-passwords');
  }

  async sendPasswordReminder(userId) {
    return this.request('/users/send-password-reminder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId })
    });
  }

  async sendCSVEmail(data) {
    return this.request('/courses/send-csv-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async sendAttendanceCSVEmail(data) {
    return this.request('/attendance/send-csv-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
  }

  async getInstructors() {
    const response = await this.request('/users');
    if (response.success && response.users) {
      const instructors = response.users.filter(user => user.role === 'instructor');
      return { success: true, instructors };
    }
    return { success: false, instructors: [] };
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE'
    });
  }

  async resetUserPassword(userId, sendEmail = true) {
    return this.request(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ sendEmail })
    });
  }


  // Course management endpoints (for admins/instructors)
  async createCourse(courseData) {
    return this.request('/courses', {
      method: 'POST',
      body: JSON.stringify(courseData)
    });
  }

  async updateCourse(courseId, courseData) {
    return this.request(`/courses/${courseId}`, {
      method: 'PUT',
      body: JSON.stringify(courseData)
    });
  }

  async deleteCourse(courseId) {
    return this.request(`/courses/${courseId}`, {
      method: 'DELETE'
    });
  }

  // Attendance API methods
  async createAttendanceSession(sessionData) {
    // sessionData can have either courseId or electiveId
    return this.request('/attendance/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  async getAttendanceSessions(courseId) {
    return this.request(`/attendance/sessions/course/${courseId}`);
  }

  async getElectiveAttendanceSessions(electiveId) {
    return this.request(`/attendance/sessions/elective/${electiveId}`);
  }

  async getAttendanceSession(sessionId) {
    return this.request(`/attendance/sessions/${sessionId}`);
  }

  async updateAttendanceRecords(sessionId, records) {
    return this.request(`/attendance/sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify({ records })
    });
  }

  async deleteAttendanceSession(sessionId) {
    return this.request(`/attendance/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }

  async getStudentAttendance(courseId) {
    return this.request(`/attendance/student/${courseId}`);
  }

  async getStudentElectiveAttendance(electiveId) {
    return this.request(`/attendance/student/elective/${electiveId}`);
  }

  async getActiveAttendanceSessions() {
    return this.request('/attendance/student/active-sessions');
  }

  async checkInAttendance(sessionId, status = 'present') {
    return this.request(`/attendance/checkin/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({ status })
    });
  }

  async updateAttendanceSession(sessionId, sessionData) {
    return this.request(`/attendance/sessions/${sessionId}/details?v=${Date.now()}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData)
    });
  }

  // Course Content API methods
  async getCourseRecordings(courseId) {
    return this.request(`/course-content/recordings/${courseId}`);
  }

  async createCourseRecording(recordingData) {
    return this.request('/course-content/recordings', {
      method: 'POST',
      body: JSON.stringify(recordingData)
    });
  }

  async getCourseAnnouncements(courseId) {
    return this.request(`/course-content/announcements/${courseId}`);
  }

  async createCourseAnnouncement(announcementData) {
    return this.request('/course-content/announcements', {
      method: 'POST',
      body: JSON.stringify(announcementData)
    });
  }

  async getCourseSyllabus(courseId) {
    return this.request(`/course-content/syllabus/${courseId}`);
  }

  async createCourseSyllabus(syllabusData) {
    return this.request('/course-content/syllabus', {
      method: 'POST',
      body: JSON.stringify(syllabusData)
    });
  }

  async getCourseSlides(courseId) {
    return this.request(`/course-content/slides/${courseId}`);
  }

  async createCourseSlide(slideData) {
    return this.request('/course-content/slides', {
      method: 'POST',
      body: JSON.stringify(slideData)
    });
  }

  // CSV Import methods
  async importCSV(file) {
    const formData = new FormData();
    formData.append('csvFile', file);

    const response = await fetch(`${this.baseURL}/csv-import/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Import failed');
    }

    return await response.json();
  }

  async confirmCSVImport(filePath, totalRecords) {
    const response = await fetch(`${this.baseURL}/csv-import/confirm-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      },
      body: JSON.stringify({ filePath, totalRecords })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Import confirmation failed');
    }

    return await response.json();
  }

  async downloadCSVTemplate() {
    const response = await fetch(`${this.baseURL}/csv-import/template`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('faithinsight_token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Course Content Management
  async getCourseContent(courseId) {
    return this.request(`/course-content/${courseId}`);
  }

  async addCourseContent(courseId, contentData) {
    // Check if contentData is FormData (for file uploads)
    const isFormData = contentData instanceof FormData;
    
    return this.request(`/course-content/${courseId}`, {
      method: 'POST',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json'
      },
      body: isFormData ? contentData : JSON.stringify(contentData)
    });
  }

  async updateCourseContent(contentId, contentData) {
    return this.request(`/course-content/${contentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contentData)
    });
  }

  async deleteCourseContent(contentId) {
    return this.request(`/course-content/${contentId}`, {
      method: 'DELETE'
    });
  }

  // Elective Content API methods
  async getElectiveContent(electiveId) {
    return this.request(`/course-content/elective/${electiveId}`);
  }

  async addElectiveContent(electiveId, contentData) {
    // Check if contentData is FormData (for file uploads)
    const isFormData = contentData instanceof FormData;
    
    return this.request(`/course-content/elective/${electiveId}`, {
      method: 'POST',
      headers: isFormData ? {} : {
        'Content-Type': 'application/json'
      },
      body: isFormData ? contentData : JSON.stringify(contentData)
    });
  }

  // Note: updateCourseContent and deleteCourseContent work for both courses and electives

  async checkUserExists(email) {
    return this.publicRequest(`/users/check-exists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });
  }

  // Product purchase API methods
  async createProductPaymentIntent(paymentData) {
    return this.request('/payments/create-product-payment-intent', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async confirmProductPayment(paymentData) {
    return this.request('/payments/confirm-product-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async getPurchaseConfirmation(paymentIntentId) {
    return this.request(`/payments/confirmation/${paymentIntentId}`);
  }

  // Payment history API methods
  async getUserEnrollments() {
    return this.request('/payments/enrollments');
  }

  async getUserPurchases() {
    return this.request('/payments/purchases');
  }

  async getCommunityStudents() {
    return this.request('/courses/community');
  }

  // Server time API methods
  async getServerTime() {
    return this.request('/attendance/time/utc-now');
  }

  // Active attendance sessions for today
  async getActiveAttendanceSessionsToday() {
    return this.request('/attendance/active-today');
  }

  // Polls API
  async createPollSession(title) {
    return this.request('/polls/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title })
    });
  }

  async addPollQuestions(sessionId, questions) {
    return this.request('/polls/questions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, questions })
    });
  }

  async activatePollQuestion(questionId) {
    return this.request(`/polls/activate/${questionId}`, { method: 'POST' });
  }

  async getPollState(code) {
    return this.request(`/polls/state/${code}`);
  }

  async listPollSessions() {
    return this.request('/polls/sessions');
  }

  async setPollGroups(sessionId, groups) {
    return this.request(`/polls/sessions/${sessionId}/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groups })
    });
  }

  async listActivePollSessions() {
    return this.request('/polls/active');
  }

  async updatePollSession(sessionId, payload) {
    return this.request(`/polls/sessions/${sessionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  async deletePollSession(sessionId) {
    return this.request(`/polls/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async updatePollQuestion(questionId, payload) {
    return this.request(`/polls/questions/${questionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  async deletePollQuestion(questionId) {
    return this.request(`/polls/questions/${questionId}`, { method: 'DELETE' });
  }

  // ============== GRADING RUBRIC METHODS ==============

  /**
   * Create new grading rubric
   */
  async createRubric(rubricData) {
    return this.request('/rubrics', {
      method: 'POST',
      body: JSON.stringify(rubricData)
    });
  }

  /**
   * Fetch rubric details
   */
  async getRubric(rubricId) {
    return this.request(`/rubrics/${rubricId}`);
  }

  /**
   * Get all rubrics for a course
   */
  async getRubricsByCourse(courseId) {
    return this.request(`/rubrics/course/${courseId}`);
  }

  /**
   * Update rubric
   */
  async updateRubric(rubricId, rubricData) {
    return this.request(`/rubrics/${rubricId}`, {
      method: 'PUT',
      body: JSON.stringify(rubricData)
    });
  }

  /**
   * Delete rubric
   */
  async deleteRubric(rubricId) {
    return this.request(`/rubrics/${rubricId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Calculate grade from category scores
   */
  async calculateRubricGrade(submissionId, rubricId, categoryScores) {
    return this.request(`/rubrics/submissions/${submissionId}/calculate-grade`, {
      method: 'POST',
      body: JSON.stringify({ rubricId, categoryScores })
    });
  }

  /**
   * Submit final grade with rubric breakdown
   */
  async submitRubricGrade(submissionId, rubricId, categoryScores, instructorNotes = '') {
    return this.request(`/rubrics/submissions/${submissionId}/submit-grade`, {
      method: 'POST',
      body: JSON.stringify({ rubricId, categoryScores, instructorNotes })
    });
  }

  /**
   * Get grade breakdown for submission
   */
  async getGradeBreakdown(submissionId) {
    return this.request(`/rubrics/submissions/${submissionId}/breakdown`);
  }

  // =====================
  // External Items Methods
  // =====================

  /**
   * Create external assignment
   */
  async createExternalAssignment(courseId, assignmentData) {
    return this.request('/external-items/assignments', {
      method: 'POST',
      body: JSON.stringify({
        courseId,
        ...assignmentData
      })
    });
  }

  /**
   * Create external quiz
   */
  async createExternalQuiz(courseId, quizData) {
    return this.request('/external-items/quizzes', {
      method: 'POST',
      body: JSON.stringify({
        courseId,
        ...quizData
      })
    });
  }

  /**
   * Update external assignment
   */
  async updateExternalAssignment(assignmentId, courseId, assignmentData) {
    return this.request(`/external-items/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify({
        courseId,
        ...assignmentData
      })
    });
  }

  /**
   * Update external quiz
   */
  async updateExternalQuiz(quizId, courseId, quizData) {
    return this.request(`/external-items/quizzes/${quizId}`, {
      method: 'PUT',
      body: JSON.stringify({
        courseId,
        ...quizData
      })
    });
  }

  /**
   * Delete external assignment
   */
  async deleteExternalAssignment(assignmentId, courseId) {
    return this.request(`/external-items/assignments/${assignmentId}`, {
      method: 'DELETE',
      body: JSON.stringify({ courseId })
    });
  }

  /**
   * Delete external quiz
   */
  async deleteExternalQuiz(quizId, courseId) {
    return this.request(`/external-items/quizzes/${quizId}`, {
      method: 'DELETE',
      body: JSON.stringify({ courseId })
    });
  }

  /**
   * Get all external items for course
   */
  async getExternalItemsForCourse(courseId) {
    return this.request(`/external-items/courses/${courseId}`);
  }

  /**
   * Upload and preview scores for assignment
   */
  async uploadAssignmentScores(assignmentId, courseId, csvFile) {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('courseId', courseId);

    return this.request(`/external-items/assignments/${assignmentId}/upload-scores`, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type with boundary
      body: formData
    });
  }

  /**
   * Upload and preview scores for quiz
   */
  async uploadQuizScores(quizId, courseId, csvFile) {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('courseId', courseId);

    return this.request(`/external-items/quizzes/${quizId}/upload-scores`, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type with boundary
      body: formData
    });
  }

  /**
   * Confirm and apply assignment scores
   */
  async confirmAssignmentScores(assignmentId, courseId, filePath) {
    return this.request(`/external-items/assignments/${assignmentId}/confirm-upload`, {
      method: 'POST',
      body: JSON.stringify({
        courseId,
        filePath
      })
    });
  }

  /**
   * Confirm and apply quiz scores
   */
  async confirmQuizScores(quizId, courseId, filePath) {
    return this.request(`/external-items/quizzes/${quizId}/confirm-upload`, {
      method: 'POST',
      body: JSON.stringify({
        courseId,
        filePath
      })
    });
  }

  /**
   * Download CSV template for external item
   */
  async downloadScoresTemplate(type, id) {
    const token = localStorage.getItem('faithinsight_token');
    const response = await fetch(`${this.baseURL}/external-items/${type}/${id}/template`, {
      method: 'GET',
      headers: {
        ...this.headers,
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.statusText}`);
    }

    return response.blob();
  }

}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
